import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthProvider, useAuth } from './AuthContext';
import { ThemeProvider, useTheme } from './ThemeContext';
import CardBrowser        from './pages/CardBrowser';
import SetsPage           from './pages/SetsPage';
import WishListPage       from './pages/WishListPage';
import CardDetailPage     from './pages/CardDetailPage';
import PortfolioDashboard from './pages/PortfolioDashboard';
import MarketPage         from './pages/MarketPage';
import DecksPage          from './pages/DecksPage';
import DeckDetailPage     from './pages/DeckDetailPage';
import DeckBuilderPage    from './pages/DeckBuilderPage';
import LoginPage          from './pages/LoginPage';
import RegisterPage       from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage  from './pages/ResetPasswordPage';
import VerifyEmailPage    from './pages/VerifyEmailPage';
import AdminPage          from './pages/AdminPage';
import PreferencesPage    from './pages/PreferencesPage';
import UserEditPage       from './pages/UserEditPage';
import './App.css';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api`;

function fmt(v: number) {
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
}

function ProtectedRoute({ children, adminOnly = false }: {
  children: React.ReactNode; adminOnly?: boolean;
}) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <div className="app-loading">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (adminOnly && user.role !== 'ADMIN') return <Navigate to="/cards" replace />;
  return <>{children}</>;
}

function Navbar() {
  const { user, logout, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [collectionValue, setCollectionValue] = useState<number | null>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);

  useEffect(() => {
    if (!user) { setCollectionValue(null); return; }
    axios.get(`${API}/portfolio/summary`)
      .then(res => setCollectionValue(res.data.totalValue))
      .catch(() => setCollectionValue(null));
  }, [user]);

  if (!user) return null;

  const logoSrc = theme === 'light'
    ? '/guardian-bazaar-logo-light.png'
    : '/guardian-bazaar-logo-dark.png';

  const firstName = user.firstName
    ? user.firstName.charAt(0).toUpperCase() + user.firstName.slice(1).toLowerCase()
    : user.username;
  const avatarLetter = (user.firstName || user.username).charAt(0).toUpperCase();

  return (
    <nav className="navbar">
      <div className="navbar-brand">
        <img src={logoSrc} alt="Guardian Bazaar" className="brand-logo" />
      </div>
      <div className="navbar-links">
        <NavLink to="/cards"     className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>Cards</NavLink>
        <NavLink to="/sets"      className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>Sets</NavLink>
        <NavLink to="/portfolio" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>Portfolio</NavLink>
        <NavLink to="/decks"     className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>Decks</NavLink>
        <NavLink to="/market"    className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>Market</NavLink>
        <NavLink to="/wishlist"  className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>Wish List</NavLink>
        {isAdmin && (
          <NavLink to="/admin"   className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>Admin</NavLink>
        )}
      </div>
      <div className="navbar-right">
        {collectionValue !== null && collectionValue > 0 && (
          <>
            <span className="collection-value">{fmt(collectionValue)}</span>
            <span className="collection-label">Collection Value</span>
          </>
        )}
        <button className="theme-toggle" onClick={toggleTheme} title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}>
          {theme === 'dark' ? '☀' : '🌙'}
        </button>
        <div className="user-menu-wrap">
          <button className="user-menu-btn" onClick={() => setShowUserMenu(v => !v)}>
            <span className="user-avatar">{avatarLetter}</span>
            <span className="user-name">{firstName}</span>
            {user.role === 'ADMIN' && <span className="user-role-badge">Admin</span>}
          </button>
          {showUserMenu && (
            <div className="user-dropdown" onMouseLeave={() => setShowUserMenu(false)}>
              <button className="user-dropdown-item"
                      onClick={() => { setShowUserMenu(false); navigate('/preferences'); }}>
                Preferences
              </button>
              <button className="user-dropdown-item" onClick={logout}>Sign Out</button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

function getLandingPath(landingPage: string): string {
  if (!landingPage || landingPage === 'collection') return '/cards';
  if (landingPage.startsWith('admin/')) return `/admin`;
  return `/${landingPage}`;
}

// Redirects "/" to "/cards", preserving any query string (e.g. ?setCodes=hob)
function RootRedirect() {
  const location = useLocation();
  return <Navigate to={`/cards${location.search}`} replace />;
}

function AppInner() {
  const { user, loading, prefs } = useAuth();

  if (loading) return <div className="app-loading">Loading…</div>;

  return (
    <div className="app">
      <Navbar />
      <main className="main-content">
        <Routes>
          <Route path="/login"            element={user ? <Navigate to={getLandingPath(prefs.landingPage)} replace /> : <LoginPage />} />
          <Route path="/register"         element={user ? <Navigate to={getLandingPath(prefs.landingPage)} replace /> : <RegisterPage />} />
          <Route path="/forgot-password"  element={user ? <Navigate to={getLandingPath(prefs.landingPage)} replace /> : <ForgotPasswordPage />} />
          <Route path="/reset-password"   element={user ? <Navigate to={getLandingPath(prefs.landingPage)} replace /> : <ResetPasswordPage />} />
          <Route path="/verify-email"     element={<VerifyEmailPage />} />
          {/* Canonical Card Browser route */}
          <Route path="/cards" element={
            <ProtectedRoute><CardBrowser /></ProtectedRoute>
          } />
          {/* Legacy root path — redirect to /cards, preserving any query string */}
          <Route path="/" element={<RootRedirect />} />
          <Route path="/cards/:cardId" element={
            <ProtectedRoute><CardDetailPage /></ProtectedRoute>
          } />
          <Route path="/sets" element={
            <ProtectedRoute><SetsPage /></ProtectedRoute>
          } />
          <Route path="/wishlist" element={
            <ProtectedRoute><WishListPage /></ProtectedRoute>
          } />
          <Route path="/portfolio" element={
            <ProtectedRoute><PortfolioDashboard /></ProtectedRoute>
          } />
          <Route path="/market" element={
            <ProtectedRoute><MarketPage /></ProtectedRoute>
          } />
          <Route path="/decks" element={
            <ProtectedRoute><DecksPage /></ProtectedRoute>
          } />
          <Route path="/decks/:deckId" element={
            <ProtectedRoute><DeckDetailPage /></ProtectedRoute>
          } />
          <Route path="/decks/:deckId/build" element={
            <ProtectedRoute><DeckBuilderPage /></ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>
          } />
          <Route path="/admin/users/new" element={
            <ProtectedRoute adminOnly><UserEditPage /></ProtectedRoute>
          } />
          <Route path="/admin/users/:userId/edit" element={
            <ProtectedRoute adminOnly><UserEditPage /></ProtectedRoute>
          } />
          <Route path="/preferences" element={
            <ProtectedRoute><PreferencesPage /></ProtectedRoute>
          } />
          <Route path="*" element={<Navigate to="/cards" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <ThemeProvider>
          <AppInner />
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
}
